import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Container,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  Tooltip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import LogoImage from '../images/dhr-poppins_.png';
import { AuthContext } from '../context/AuthContext';
import { 
  LogOut, 
  User, 
  Settings, 
  HelpCircle,
  Mail,
  Phone
} from 'lucide-react';
import { AdminPanelSettings } from '@mui/icons-material';

// Use REACT_APP_API_URL_DEVELOPMENT if available, otherwise fallback to REACT_APP_API_URL
import { API_URL } from '../utils/apiConfig';
import AssignmentIcon from '@mui/icons-material/Assignment';

const V2_FEATURES = process.env.REACT_APP_V2_FEATURES === 'true';
// Legacy flag for backward compatibility
const SCHED_FEATURES = V2_FEATURES || process.env.REACT_APP_SCHED_FEATURES === 'true';

const Nav = ({ onStoreChange }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentStore, setCurrentStore] = useState('All');
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [userName, setUserName] = useState(null);
  const [loadingEmployee, setLoadingEmployee] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [dropdownAnchors, setDropdownAnchors] = useState({});
  
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { 
    isAuthenticated, 
    userRole, 
    employeeName,
    employeeId,
    logout 
  } = useContext(AuthContext);

  // Define role-based page access with grouped navigation
  const navigationStructure = {
    admin: [
      { path: '/dashboard', name: 'DASHBOARD', type: 'link' },
      {
        name: 'TEAM MANAGEMENT',
        type: 'dropdown',
        items: [
          { path: '/employees', name: 'Employees' },
          ...(V2_FEATURES ? [
            { path: '/staffing-requirements', name: 'Staffing Requirements' },
            { path: '/scheduling', name: 'Scheduling' },
            { path: '/availability', name: 'Employee Availability' },
            { path: '/v2/forms', name: 'Forms v2' },
            { path: '/v2/form-builder', name: 'Form Builder' }
          ] : []),
          { path: '/letters', name: 'Send Letters' },
          { path: '/manager/documents', name: 'Form Review' },
          { path: '/incomplete-forms', name: 'Incomplete Forms' },
        ]
      },
      {
        name: 'PERSONAL',
        type: 'dropdown',
        items: [
          { path: '/employee_forms', name: 'My Forms' },
          ...(V2_FEATURES ? [
            { path: '/v2/forms', name: 'Forms v2' },
          ] : []),
          { path: '/my-letters', name: 'My Letters' },
        ]
      },
      {
        name: 'OPERATIONS',
        type: 'dropdown',
        items: [
          { path: '/requests', name: 'Requests' },
          { path: '/payroll_notes_calendar', name: 'Payroll Calendar' },
        ]
      },
    ],
    manager: [
      { path: '/dashboard', name: 'DASHBOARD', type: 'link' },
      {
        name: 'TEAM MANAGEMENT',
        type: 'dropdown',
        items: [
          { path: '/employees', name: 'Employees' },
          ...(V2_FEATURES ? [
            { path: '/staffing-requirements', name: 'Staffing Requirements' },
            { path: '/scheduling', name: 'Scheduling' },
            { path: '/availability', name: 'Employee Availability' },
            { path: '/v2/forms', name: 'Forms v2' },
            { path: '/v2/form-builder', name: 'Form Builder' }
          ] : []),
          { path: '/letters', name: 'Send Letters' },
          { path: '/manager/documents', name: 'Form Review' },
          { path: '/incomplete-forms', name: 'Incomplete Forms' },
        ]
      },
      {
        name: 'PERSONAL',
        type: 'dropdown',
        items: [
          { path: '/employee_forms', name: 'My Forms' },
          ...(V2_FEATURES ? [
            { path: '/v2/forms', name: 'Forms v2' },
          ] : []),
          { path: '/my-letters', name: 'My Letters' },
        ]
      },
      {
        name: 'OPERATIONS',
        type: 'dropdown',
        items: [
          { path: '/requests', name: 'Requests' },
          { path: '/payroll_notes_calendar', name: 'Payroll Calendar' },
        ]
      },
    ],
    user: [
      { 
        path: '/employee_forms', 
        name: 'FORMS', 
        type: 'link',
        tooltip: 'Complete and manage your employment forms, including W-4, I-9, and other required documents'
      },
      ...(V2_FEATURES ? [{
        path: '/v2/forms',
        name: 'FORMS V2',
        type: 'link',
        tooltip: 'Preview the new dynamic Forms v2 system with templates and combined submissions'
      }] : []),
      { 
        path: '/requests', 
        name: 'REQUESTS', 
        type: 'link',
        tooltip: 'Submit check reissue requests and other HR requests'
      },
      { 
        path: '/my-letters', 
        name: 'MY LETTERS', 
        type: 'link',
        tooltip: 'View and download important letters and documents from your employer'
      },
    ]
  };
  
  // For backward compatibility
  const pageNamesByRole = {
    admin: {
      '/dashboard': 'DASHBOARD',
      '/employees': 'EMPLOYEES',
      '/letters': 'LETTERS',
      '/checks': 'CHAT & REQUESTS',
      '/payroll_notes_calendar': 'PAYROLL CAL',
      '/employee_forms': 'MY FORMS',
      '/my-letters': 'MY LETTERS',
      '/manager/documents': 'FORM REVIEW',
      '/incomplete-forms': 'INCOMPLETE FORMS',
    },
    manager: {
      '/dashboard': 'DASHBOARD',
      '/employees': 'EMPLOYEES',
      '/letters': 'LETTERS',
      '/checks': 'CHAT & REQUESTS',
      '/payroll_notes_calendar': 'PAYROLL CAL',
      '/employee_forms': 'MY FORMS',
      '/my-letters': 'MY LETTERS',
      '/manager/documents': 'FORM REVIEW',
      '/incomplete-forms': 'INCOMPLETE FORMS',
    },
    user: {
      '/employee_forms': 'FORMS',
      '/checks': 'CHAT & REQUESTS',
      '/my-letters': 'MY LETTERS',
    }
  };

  // Direct fetch from the API to get the employee's name
  const fetchEmployeeName = async () => {
    try {
      // Get employee ID from various sources
      const employeeId = localStorage.getItem('employeeId');
      if (!employeeId) {
        console.warn("No employee ID available to fetch name");
        return null;
      }
      
      // Get token
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn("No token available to fetch employee data");
        return null;
      }
      
      console.log(`Nav - Directly fetching employee data for ID: ${employeeId}`);
      
      const API_URL = process.env.NODE_ENV === 'development' 
        ? process.env.REACT_APP_API_URL_DEVELOPMENT 
        : process.env.REACT_APP_API_URL;
      
      // Direct fetch from employees table endpoint
      const response = await fetch(`${API_URL}/employees/${employeeId}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch employee: ${response.status}`);
      }
      
      const employee = await response.json();
      console.log("Nav - Employee data fetched directly:", employee);
      
      if (employee?.name) {
        // Store and return the full name
        localStorage.setItem('employeeName', employee.name);
        return employee.name;
      } else if (employee?.firstName && employee?.lastName) {
        const fullName = `${employee.firstName} ${employee.lastName}`;
        localStorage.setItem('employeeName', fullName);
        return fullName;
      }
      
      return null;
    } catch (error) {
      console.error("Nav - Error fetching employee name:", error);
      return null;
    }
  };
  
  // Check for impersonation mode
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setIsImpersonating(payload.isImpersonating || false);
      } catch (e) {
        setIsImpersonating(false);
      }
    }
  }, [isAuthenticated]);

  // Initialization effect for user name
  useEffect(() => {
    // Debug all available data sources
    console.log('Debug Nav - AUTH Data:', {
      contextData: { employeeName, userRole },
      storedEmployeeName: localStorage.getItem('employeeName'),
      user: localStorage.getItem('user'),
      userRole: localStorage.getItem('userRole'),
      token: localStorage.getItem('token') ? 'exists' : 'missing'
    });
    
    const loadName = async () => {
      // Check if the current name is just the role
      const storedName = localStorage.getItem('employeeName');
      const lowerStoredName = storedName?.toLowerCase();
      
      if (lowerStoredName === 'admin' || lowerStoredName === 'manager' || lowerStoredName === 'user') {
        console.log("Nav - Detected role as name, trying to fetch real name from API");
        const apiName = await fetchEmployeeName();
        if (apiName) {
          console.log("Nav - Successfully retrieved real name from API:", apiName);
          setUserName(apiName);
          return;
        }
      }
      
      // First priority: Check localStorage
      if (storedName && storedName !== 'null' && storedName !== 'null null' && 
          lowerStoredName !== 'admin' && lowerStoredName !== 'manager' && lowerStoredName !== 'user') {
        console.log("Nav - Using stored name from localStorage:", storedName);
        setUserName(storedName);
        return;
      }
  
      // Second priority: Use employeeName from context if valid
      if (employeeName && employeeName !== 'null' && employeeName !== 'null null' &&
          employeeName.toLowerCase() !== 'admin' && employeeName.toLowerCase() !== 'manager' && 
          employeeName.toLowerCase() !== 'user') {
        console.log("Nav - Using employee name from context:", employeeName);
        setUserName(employeeName);
        return;
      }
      
      // Third priority: Try to fetch directly from API
      console.log("Nav - Trying to fetch name directly from API");
      const fetchedName = await fetchEmployeeName();
      if (fetchedName) {
        console.log("Nav - Successfully retrieved name from API:", fetchedName);
        setUserName(fetchedName);
        return;
      }
  
      // Fourth priority: Try to extract a name from localStorage user data
      try {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        console.log("Nav - parsed user data:", userData);
        
        // Check for name field
        if (userData.name) {
          console.log("Nav - Using name from userData:", userData.name);
          setUserName(userData.name);
          return;
        }
        
        // Check for firstName and lastName fields
        if (userData.firstName || userData.lastName) {
          const fullName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
          console.log("Nav - Using firstName/lastName from userData:", fullName);
          setUserName(fullName);
          return;
        }
        
        // Check for first_name and last_name fields
        if (userData.first_name || userData.last_name) {
          const fullName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
          console.log("Nav - Using first_name/last_name from userData:", fullName);
          setUserName(fullName);
          return;
        }
        
        // Try email if available
        if (userData.email) {
          const emailName = userData.email.split('@')[0];
          // Capitalize and format email username
          const formattedName = emailName
            .split('.')
            .map(part => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ');
          
          console.log("Nav - Using formatted email as name:", formattedName);
          setUserName(formattedName);
          return;
        }
      } catch (error) {
        console.error("Nav - Error parsing user data:", error);
      }
  
      // Fifth priority: Hardcode the name based on SQL data
      console.log("Nav - Using hardcoded name from SQL data");
      setUserName("Jonny Dailey");
    };
    
    loadName();
  }, [employeeName, userRole]);

  // Get pages based on user role
  const getPageNames = () => {
    return pageNamesByRole[userRole] || {};
  };

  const handleLogout = () => {
    setUserMenuAnchor(null);
    logout();
    navigate('/login');
  };

  const handleOpenHelpDialog = () => {
    setHelpDialogOpen(true);
    setUserMenuAnchor(null); // Close the user menu
  };

  const handleCloseHelpDialog = () => {
    setHelpDialogOpen(false);
  };

  const currentPageName = getPageNames()[location.pathname] || 'DASHBOARD';

  useEffect(() => {
    const savedStore = localStorage.getItem('selectedStore');
    if (savedStore) {
      setCurrentStore(savedStore);
      onStoreChange(savedStore);
    }
  }, [onStoreChange]);

  const handleStoreChange = (storeName) => {
    setCurrentStore(storeName);
    localStorage.setItem('selectedStore', storeName);
    onStoreChange(storeName);
    navigate('/employees');
  };

  const toggleDrawer = (open) => {
    setDrawerOpen(open);
  };

  const openUserMenu = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const closeUserMenu = () => {
    setUserMenuAnchor(null);
  };

  const isActive = (path) => location.pathname === path;
  
  // Get employee initials for avatar
  const getInitials = () => {
    if (!userName) return "U";
    
    const names = userName.split(" ");
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };
  
  // Dropdown handlers
  const handleDropdownOpen = (event, dropdownName) => {
    setDropdownAnchors({ ...dropdownAnchors, [dropdownName]: event.currentTarget });
  };
  
  const handleDropdownClose = (dropdownName) => {
    setDropdownAnchors({ ...dropdownAnchors, [dropdownName]: null });
  };
  
  // Check if any path in dropdown is active
  const isDropdownActive = (items) => {
    return items.some(item => location.pathname === item.path);
  };

  if (isAuthenticated === false) return null;

  // Get the appropriate pages for the current user role
  const pageNames = getPageNames();

  return (
    <>
      {/* AppBar - Fixed at the top */}
      <AppBar 
        position="fixed" 
        sx={{ 
          bgcolor: '#276a78',
          boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
          zIndex: (theme) => theme.zIndex.drawer + 2000, // Higher than drawer
          width: '100%',
          left: 0,
          right: 0,
          top: 0
        }}
      >
        <Container maxWidth={false} sx={{ height: '100%' }}>
          <Toolbar
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 0,
              minHeight: { xs: '48px', md: '64px' },
              height: '100%',
              position: 'relative'
            }}
            variant="dense"
            disableGutters
          >
            {/* Mobile menu button - Show first on mobile */}
            <IconButton
              color="inherit"
              sx={{ display: { xs: 'flex', md: 'none' }, ml: 1 }}
              onClick={() => toggleDrawer(true)}
            >
              <MenuIcon />
            </IconButton>
            
            {/* Logo - Centered on mobile, left-aligned on desktop */}
            <Box
              component={Link}
              to={userRole === 'user' ? '/employee_forms' : '/dashboard'}
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                position: { xs: 'absolute', md: 'relative' },
                left: { xs: '50%', md: 'auto' },
                transform: { xs: 'translateX(-50%)', md: 'none' },
                ml: { xs: 0, md: 2 }
              }}
            >
              <img
                src={LogoImage}
                alt="DaileyHR"
                style={{
                  width: isMobile ? '60px' : '90px',
                  borderRadius: '0px',
                  padding: '0px',
                }}
              />
            </Box>

            {/* Desktop Links */}
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                flex: 1,
                ml: 4
              }}
            >
              {navigationStructure[userRole]?.map((item, index) => {
                if (item.type === 'link') {
                  const button = (
                    <Button
                      key={item.path}
                      color="inherit"
                      component={Link}
                      to={item.path}
                      sx={{
                        fontFamily: 'Poppins, sans-serif',
                        mx: 0.5,
                        px: 1.5,
                        fontSize: '0.85rem',
                        fontWeight: 400,
                        letterSpacing: '0.5px',
                        color: 'white',
                        position: 'relative',
                        height: '100%',
                        borderRadius: 0,
                        textAlign: 'center',
                        borderBottom: isActive(item.path) ? '4px solid white' : 'none',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                        }
                      }}
                    >
                      {item.name}
                    </Button>
                  );
                  
                  // Wrap with tooltip if user role and tooltip exists
                  if (userRole === 'user' && item.tooltip) {
                    return (
                      <Tooltip key={item.path} title={item.tooltip} arrow placement="bottom">
                        {button}
                      </Tooltip>
                    );
                  }
                  
                  return button;
                } else if (item.type === 'dropdown') {
                  const dropdownId = `dropdown-${index}`;
                  const isOpen = Boolean(dropdownAnchors[dropdownId]);
                  const hasActiveItem = isDropdownActive(item.items);
                  
                  return (
                    <Box key={dropdownId} sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                      <Button
                        color="inherit"
                        onClick={(e) => handleDropdownOpen(e, dropdownId)}
                        endIcon={<KeyboardArrowDownIcon sx={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />}
                        sx={{
                          fontFamily: 'Poppins, sans-serif',
                          mx: 0.5,
                          px: 1.5,
                          fontSize: '0.85rem',
                          fontWeight: 400,
                          letterSpacing: '0.5px',
                          color: 'white',
                          position: 'relative',
                          height: '100%',
                          borderRadius: 0,
                          textAlign: 'center',
                          borderBottom: hasActiveItem ? '4px solid white' : 'none',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          }
                        }}
                      >
                        {item.name}
                      </Button>
                      <Menu
                        anchorEl={dropdownAnchors[dropdownId]}
                        open={isOpen}
                        onClose={() => handleDropdownClose(dropdownId)}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'left',
                        }}
                        sx={{
                          '& .MuiPaper-root': {
                            mt: 0.5,
                            minWidth: 180,
                            boxShadow: '0px 5px 15px rgba(0,0,0,0.2)',
                          }
                        }}
                      >
                        {item.items.map((subItem) => (
                          <MenuItem
                            key={subItem.path}
                            component={Link}
                            to={subItem.path}
                            onClick={() => handleDropdownClose(dropdownId)}
                            selected={isActive(subItem.path)}
                            sx={{
                              fontSize: '0.875rem',
                              py: 1,
                              '&.Mui-selected': {
                                bgcolor: 'rgba(39, 106, 120, 0.08)',
                                '&:hover': {
                                  bgcolor: 'rgba(39, 106, 120, 0.12)',
                                }
                              },
                              '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.04)',
                              }
                            }}
                          >
                            {subItem.name}
                          </MenuItem>
                        ))}
                      </Menu>
                    </Box>
                  );
                }
                return null;
              })}
            </Box>

            
            {/* User menu button */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                mr: { xs: 2, sm: 3, md: 4 }, // Increased right margin for admin tools space
                ml: { xs: 0, md: 2 }, // No left margin on mobile
              }}
            >
              <Button
                onClick={openUserMenu}
                color="inherit"
                endIcon={<KeyboardArrowDownIcon />}
                sx={{
                  borderRadius: '20px',
                  pl: 1,
                  pr: 1.5,
                  maxWidth: { xs: '180px', sm: '220px', md: '100%' }, // Limit width on smaller screens
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                <Avatar
                  sx={{
                    width: { xs: 24, md: 28 },
                    height: { xs: 24, md: 28 },
                    bgcolor: 'white',
                    color: '#276a78',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    mr: 0.75
                  }}
                >
                  {loadingEmployee ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    getInitials()
                  )}
                </Avatar>
                <Typography
                  sx={{
                    color: 'white',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '0.85rem',
                    fontWeight: 'normal',
                    display: { xs: 'none', sm: 'block' }
                  }}
                >
                  {userName}
                </Typography>
              </Button>

              {/* User dropdown menu */}
              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={closeUserMenu}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.12))',
                    mt: 1.5,
                    width: 220,
                    '& .MuiAvatar-root': {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1,
                    },
                  }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5 }}>
                    {userName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {userRole === 'user' ? 'Employee' : (userRole?.charAt(0).toUpperCase() + userRole?.slice(1)) || 'Employee'}
                  </Typography>
                </Box>
                <Divider />
                <MenuItem component={Link} to="/employee_forms">
                  <ListItemIcon>
                    <User size={18} />
                  </ListItemIcon>
                  <ListItemText>Profile</ListItemText>
                </MenuItem>
                {userRole === 'admin' && (
                  <>
                    <MenuItem onClick={() => {
                      closeUserMenu();
                      // Trigger the admin tools menu from ImpersonationBar
                      const event = new CustomEvent('openAdminTools');
                      window.dispatchEvent(event);
                    }}>
                      <ListItemIcon>
                        <AdminPanelSettings size={18} />
                      </ListItemIcon>
                      <ListItemText>Admin Tools</ListItemText>
                    </MenuItem>
                  </>
                )}
                <MenuItem onClick={handleOpenHelpDialog}>
                  <ListItemIcon>
                    <HelpCircle size={18} />
                  </ListItemIcon>
                  <ListItemText>Help</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogOut size={18} color="#d32f2f" />
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ color: '#d32f2f' }}>
                    Logout
                  </ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Spacer Box to push content below the AppBar */}
      <Box 
        sx={{ 
          height: { xs: '48px', md: '64px' }, 
          width: '100%',
          flexShrink: 0 // Prevent the spacer from shrinking
        }} 
      />
        
      {/* Mobile Drawer - positioned correctly with respect to AppBar */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => toggleDrawer(false)}
        variant="temporary"
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          zIndex: (theme) => theme.zIndex.drawer - 1, // Below AppBar
          '& .MuiDrawer-paper': {
            width: 300,
            boxSizing: 'border-box',
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            overflowX: 'hidden',
            pt: '56px', // Add padding top to account for AppBar height
            top: 0,
            height: '100vh',
            zIndex: (theme) => theme.zIndex.drawer - 1 // Below AppBar
          },
          '& .MuiBackdrop-root': {
            top: 0,
            zIndex: (theme) => theme.zIndex.drawer - 2 // Below everything
          }
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper'
          }}
          role="presentation"
        >
          {/* Drawer Header */}
          <Box sx={{ 
            p: 2, 
            bgcolor: '#276a78',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
          onClick={(e) => e.stopPropagation()}>
            <Typography variant="h6" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>
              Menu
            </Typography>
            <IconButton 
              onClick={() => toggleDrawer(false)}
              sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Box
            sx={{ flex: 1, overflow: 'auto' }}
          >
            <List sx={{ pt: 1, pb: 0 }}>
            {navigationStructure[userRole]?.map((item, index) => {
              if (item.type === 'link') {
                return (
                  <ListItem
                    button
                    component={Link}
                    to={item.path}
                    key={item.path}
                    selected={isActive(item.path)}
                    onClick={() => toggleDrawer(false)}
                    sx={{ 
                      py: 1.5,
                      px: 3,
                      borderLeft: isActive(item.path) ? '4px solid #276a78' : '4px solid transparent',
                      bgcolor: isActive(item.path) ? 'rgba(39, 106, 120, 0.08)' : 'transparent',
                      '&:hover': {
                        bgcolor: isActive(item.path) ? 'rgba(39, 106, 120, 0.08)' : 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <ListItemText 
                      primary={item.name}
                      secondary={userRole === 'user' && item.tooltip ? item.tooltip : null}
                      primaryTypographyProps={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: isActive(item.path) ? '600' : '400',
                        color: isActive(item.path) ? '#276a78' : 'text.primary',
                        fontSize: '0.95rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    />
                  </ListItem>
                );
              } else if (item.type === 'dropdown') {
                return (
                  <Box key={`dropdown-${index}`}>
                    <ListItem
                      sx={{ 
                        py: 1,
                        px: 3,
                        cursor: 'default',
                        '&:hover': {
                          bgcolor: 'transparent'
                        }
                      }}
                    >
                      <ListItemText 
                        primary={item.name}
                        primaryTypographyProps={{
                          fontFamily: 'Poppins, sans-serif',
                          fontWeight: '600',
                          color: 'text.secondary',
                          fontSize: '0.8rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}
                      />
                    </ListItem>
                    {item.items.map((subItem) => (
                      <ListItem
                        button
                        component={Link}
                        to={subItem.path}
                        key={subItem.path}
                        selected={isActive(subItem.path)}
                        onClick={() => toggleDrawer(false)}
                        sx={{ 
                          py: 1.2,
                          pl: 5,
                          pr: 3,
                          borderLeft: isActive(subItem.path) ? '4px solid #276a78' : '4px solid transparent',
                          bgcolor: isActive(subItem.path) ? 'rgba(39, 106, 120, 0.08)' : 'transparent',
                          '&:hover': {
                            bgcolor: isActive(subItem.path) ? 'rgba(39, 106, 120, 0.08)' : 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      >
                        <ListItemText 
                          primary={subItem.name}
                          primaryTypographyProps={{
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: isActive(subItem.path) ? '500' : '400',
                            color: isActive(subItem.path) ? '#276a78' : 'text.primary',
                            fontSize: '0.9rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        />
                      </ListItem>
                    ))}
                  </Box>
                );
              }
              return null;
            })}
          </List>
          <Divider />
          <List sx={{ pb: 2 }}>
            {userRole === 'admin' && (
              <ListItem 
                button 
                onClick={() => {
                  toggleDrawer(false);
                  const event = new CustomEvent('openAdminTools');
                  window.dispatchEvent(event);
                }}
                sx={{ px: 3, py: 1.5 }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <AdminPanelSettings sx={{ fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary="Admin Tools"
                  primaryTypographyProps={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '0.95rem'
                  }}
                />
              </ListItem>
            )}
            <ListItem 
              button 
              onClick={handleOpenHelpDialog}
              sx={{ px: 3, py: 1.5 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <HelpCircle size={20} />
              </ListItemIcon>
              <ListItemText
                primary="Help"
                primaryTypographyProps={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '0.95rem'
                }}
              />
            </ListItem>
            <ListItem 
              button 
              onClick={handleLogout}
              sx={{ px: 3, py: 1.5 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LogOut size={20} color="#d32f2f" />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{
                  fontFamily: 'Poppins, sans-serif',
                  color: '#d32f2f',
                  fontSize: '0.95rem'
                }}
              />
            </ListItem>
          </List>
          </Box>
        </Box>
      </Drawer>

      {/* Help Dialog */}
      <Dialog 
        open={helpDialogOpen} 
        onClose={handleCloseHelpDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          bgcolor: '#276a78', 
          color: 'white',
          fontFamily: 'Poppins, sans-serif',
          fontWeight: 'bold',
          py: 2
        }}>
          Contact Support
        </DialogTitle>
        <DialogContent sx={{ py: 3, px: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Poppins, sans-serif', color: '#276a78' }}>
            Need assistance with Dailey HR?
          </Typography>
          
          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center' }}>
            <Mail size={20} color="#276a78" />
            <Typography variant="body1" sx={{ ml: 2, fontFamily: 'Poppins, sans-serif' }}>
              Email: <a href="mailto:support@dailey.llc" style={{ color: '#276a78', textDecoration: 'none', fontWeight: 'bold' }}>
                support@dailey.llc
              </a>
            </Typography>
          </Box>
          
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
            <Phone size={20} color="#276a78" />
            <Typography variant="body1" sx={{ ml: 2, fontFamily: 'Poppins, sans-serif' }}>
              Call: <a href="tel:18555324539" style={{ color: '#276a78', textDecoration: 'none', fontWeight: 'bold' }}>
                1-855-532-4539
              </a> (<a href="tel:18555324539" style={{ color: '#276a78', textDecoration: 'none', fontWeight: 'bold' }}>
                1-855-5-DAILEY)
              </a>
            </Typography>
          </Box>
          
          <Typography variant="body2" sx={{ mt: 4, color: 'text.secondary', fontFamily: 'Poppins, sans-serif' }}>
            Our support team is available Monday-Friday, 9am-5pm EST.
            We typically respond to all inquiries within 24 hours.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseHelpDialog} 
            sx={{ 
              color: '#276a78', 
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 'medium'
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Nav;
